import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { PurchaseOrderFieldItem } from '../purchase-order-config.config';

// ============================================================================
// LOCAL STORAGE KEY
// ============================================================================

const STORAGE_KEY = 'purchase_order_config';

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_PURCHASE_ORDER_FIELDS: PurchaseOrderFieldItem[] = [
  {
    fieldKey: 'purchaseRequest',
    fieldName: 'purchase_order_config.fields.purchase_request',
    isActive: false,
    isRequired: false,
    description: 'purchase_order_config.descriptions.purchase_request',
  },
  {
    fieldKey: 'series',
    fieldName: 'purchase_order_config.fields.series',
    isActive: true,
    isRequired: true,
    description: 'purchase_order_config.descriptions.series',
  },
  {
    fieldKey: 'vendorCode',
    fieldName: 'purchase_order_config.fields.vendor',
    isActive: true,
    isRequired: true,
    description: 'purchase_order_config.descriptions.vendor',
  },
  {
    fieldKey: 'description',
    fieldName: 'purchase_order_config.fields.description',
    isActive: true,
    isRequired: true,
    description: 'purchase_order_config.descriptions.description',
  },
  {
    fieldKey: 'deliveryDate',
    fieldName: 'purchase_order_config.fields.delivery_date',
    isActive: true,
    isRequired: true,
    description: 'purchase_order_config.descriptions.delivery_date',
  },
];

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderConfigService {
  constructor() {}

  /**
   * Get purchase order field configurations from local storage or API
   */
  getPurchaseOrderFields(): Observable<PurchaseOrderFieldItem[]> {
    // Try to get from local storage first
    const storedConfig = this.getFromLocalStorage();
    if (storedConfig !== null) {
      return of(storedConfig);
    }

    // If not in local storage, get from mock and save to local storage
    return of([...MOCK_PURCHASE_ORDER_FIELDS]).pipe(
      delay(300),
      tap((fields) => this.saveToLocalStorage(fields))
    );
  }

  /**
   * Get purchase order field configurations from local storage only
   */
  getPurchaseOrderFieldsFromStorage(): PurchaseOrderFieldItem[] | null {
    return this.getFromLocalStorage();
  }

  /**
   * Save purchase order field configurations to local storage
   */
  savePurchaseOrderFieldsToStorage(fields: PurchaseOrderFieldItem[]): void {
    this.saveToLocalStorage(fields);
  }

  /**
   * Get configuration from local storage
   */
  private getFromLocalStorage(): PurchaseOrderFieldItem[] | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as PurchaseOrderFieldItem[];
      }
    } catch (error) {
      console.error('Error reading from local storage:', error);
    }
    return null;
  }

  /**
   * Save configuration to local storage
   */
  private saveToLocalStorage(fields: PurchaseOrderFieldItem[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fields));
    } catch (error) {
      console.error('Error saving to local storage:', error);
    }
  }

  /**
   * Get purchase order field by key
   */
  getPurchaseOrderField(
    fieldKey: string
  ): Observable<PurchaseOrderFieldItem | undefined> {
    const field = MOCK_PURCHASE_ORDER_FIELDS.find(
      (item) => item.fieldKey === fieldKey
    );
    return of(field).pipe(delay(200));
  }

  /**
   * Update purchase order field configuration
   */
  updatePurchaseOrderField(
    fieldKey: string,
    updates: { isActive?: boolean; isRequired?: boolean }
  ): Observable<PurchaseOrderFieldItem> {
    // Mock implementation
    const index = MOCK_PURCHASE_ORDER_FIELDS.findIndex(
      (item) => item.fieldKey === fieldKey
    );
    if (index === -1) {
      throw new Error('Purchase order field not found');
    }

    const updatedField: PurchaseOrderFieldItem = {
      ...MOCK_PURCHASE_ORDER_FIELDS[index],
      ...(updates.isActive !== undefined && { isActive: updates.isActive }),
      ...(updates.isRequired !== undefined && {
        isRequired: updates.isRequired,
      }),
    };

    MOCK_PURCHASE_ORDER_FIELDS[index] = updatedField;
    // Update local storage
    this.saveToLocalStorage([...MOCK_PURCHASE_ORDER_FIELDS]);
    return of(updatedField).pipe(delay(500));
  }

  /**
   * Update multiple purchase order field configurations
   */
  updatePurchaseOrderFields(
    fields: PurchaseOrderFieldItem[]
  ): Observable<PurchaseOrderFieldItem[]> {
    fields.forEach((field) => {
      const index = MOCK_PURCHASE_ORDER_FIELDS.findIndex(
        (item) => item.fieldKey === field.fieldKey
      );
      if (index !== -1) {
        MOCK_PURCHASE_ORDER_FIELDS[index] = {
          ...MOCK_PURCHASE_ORDER_FIELDS[index],
          isActive: field.isActive,
          isRequired: field.isRequired,
        };
      }
    });

    // Update local storage
    this.saveToLocalStorage([...MOCK_PURCHASE_ORDER_FIELDS]);
    return of([...MOCK_PURCHASE_ORDER_FIELDS]).pipe(delay(500));
  }

  /**
   * Reset purchase order fields to default configuration
   */
  resetPurchaseOrderFields(): Observable<PurchaseOrderFieldItem[]> {
    // Reset to initial mock data
    MOCK_PURCHASE_ORDER_FIELDS.forEach((field, index) => {
      const defaultField = [
        {
          fieldKey: 'purchaseRequest',
          fieldName: 'purchase_order_config.fields.purchase_request',
          isActive: true,
          isRequired: true,
          description: 'purchase_order_config.descriptions.purchase_request',
        },
        {
          fieldKey: 'series',
          fieldName: 'purchase_order_config.fields.series',
          isActive: true,
          isRequired: true,
          description: 'purchase_order_config.descriptions.series',
        },
        {
          fieldKey: 'vendorCode',
          fieldName: 'purchase_order_config.fields.vendor',
          isActive: true,
          isRequired: true,
          description: 'purchase_order_config.descriptions.vendor',
        },
        {
          fieldKey: 'description',
          fieldName: 'purchase_order_config.fields.description',
          isActive: true,
          isRequired: true,
          description: 'purchase_order_config.descriptions.description',
        },
        {
          fieldKey: 'deliveryDate',
          fieldName: 'purchase_order_config.fields.delivery_date',
          isActive: true,
          isRequired: true,
          description: 'purchase_order_config.descriptions.delivery_date',
        },
      ][index];
      MOCK_PURCHASE_ORDER_FIELDS[index] = defaultField;
    });

    // Update local storage
    this.saveToLocalStorage([...MOCK_PURCHASE_ORDER_FIELDS]);
    return of([...MOCK_PURCHASE_ORDER_FIELDS]).pipe(delay(300));
  }
}
